import { ChartColors as LibChartColors } from '@ecosystem/newron-design-system/build/exports/colors';
import { colors } from '@ecosystem/newron-design-system';
import { ChartColors, DataSet } from '../../design/charts';
import {
  ChangeType,
  ColumnRelationship,
  DataColumn,
  DataRow,
  DataType,
  DataWarning,
  Entity,
  FormatHint,
  IdentifierColumn,
  KpiWithChangeColumn,
} from '../../types';
import { DataStore } from '../../data/store';
import { Formatting } from '../../data/formatting';
import { DEFAULT_PLK, DEFAULT_QUANTITY } from '../constants';

const REVENUE = 'revenue';
const SHARE_REVENUE = 'share_revenue';
const AVERAGE_PRICE = 'average_price';
const PRICE_INDEX = 'price_index';
const defaultAveragePrice = (quantityType: string) =>
  quantityType === DEFAULT_PLK ? `${AVERAGE_PRICE}_${quantityType}` : AVERAGE_PRICE;
const defaultPriceIndex = (quantityType: string) =>
  quantityType === DEFAULT_PLK ? `${PRICE_INDEX}_${quantityType}` : PRICE_INDEX;
const shareQuantity = (quantityType: string) => `share_${quantityType}`;

export interface MarketKpiType {
  revenue: string;
  quantity: string;
  shareRevenue: string;
  shareQuantity: string;
  averagePrice: string;
  priceIndex: string;
}

export const createMarketKpi = (quantityType: string = DEFAULT_QUANTITY): MarketKpiType => {
  return {
    revenue: REVENUE,
    quantity: quantityType,
    shareRevenue: SHARE_REVENUE,
    shareQuantity: shareQuantity(quantityType),
    averagePrice: defaultAveragePrice(quantityType),
    priceIndex: defaultPriceIndex(quantityType),
  };
};

export type ChartData = {
  yColumn: DataColumn;
  updatedYColumn?: DataColumn;
  dataSets: DataSet[];
  totalsDataSet?: DataSet;
  dataLabels: (string | string[])[];
  hasData?: boolean;
  warnings?: DataWarning[];
};

export class ColorHelper {
  private helper: LibChartColors;
  private brandColors: Record<string, string> = {};
  private defaultColors: Record<string, string> = {};
  private isBrands = false;

  constructor(xColumnName: string, xRowValues: string[]) {
    this.helper = colors.ChartColors.getInstance();
    if (xColumnName.includes('brand')) {
      this.brandColors = this.helper.getBrandColorMap(xRowValues);
      this.isBrands = true;
    } else {
      xRowValues.forEach((value, index) => {
        this.defaultColors[value] = this.helper.standardColors[this.getStandardColorIndex(index)];
      });
    }
  }

  getStandardColorIndex(index: number) {
    // only 30 colours available!!
    return Math.min(index, this.helper.standardColors.length - 1);
  }

  getColors(rowXValue: string, index: number, bgColor?: string): [bgColor: string, textColor: string] {
    let backgroundColor = bgColor || '';
    if (rowXValue.toLocaleLowerCase() === 'others') {
      backgroundColor = ChartColors.othersBg;
    } else if (this.isBrands) {
      backgroundColor = this.brandColors[rowXValue];
      if (!backgroundColor) {
        backgroundColor = this.helper.standardColors[this.getStandardColorIndex(index)];
      }
    } else {
      backgroundColor = this.defaultColors[rowXValue];
      if (!backgroundColor) {
        backgroundColor = this.helper.standardColors[this.getStandardColorIndex(index)];
      }
    }

    return [backgroundColor, this.helper.getContrastTextColor(backgroundColor)];
  }
}

export function getColumnsForChart(columns: DataColumn[]): Entity[] {
  return columns
    .filter(col => (col.dataType === DataType.Decimal || col.dataType === DataType.Integer) && col.arrayLength !== null)
    .map(col => ({
      value: col.name,
      label: col.label,
    }));
}

export function makeDataSet(
  row: DataRow,
  options: {
    xColumn: DataColumn;
    yColumn: DataColumn;
    withChange: boolean;
    index: number;
    colorHelper: ColorHelper;
    bgColor?: string;
  }
): DataSet {
  const { xColumn, yColumn, withChange, index, colorHelper, bgColor } = options;
  const set: DataSet = {
    values: {
      raw: [],
      formatted: [],
      fillColor: [],
      textColor: [],
    },
  };

  if (withChange) {
    set.changes = {
      raw: [],
      formatted: [],
      textColor: [],
    };
  }

  const name = String(row.values[xColumn.name]);
  const [backgroundColor, textColor] = colorHelper.getColors(name, index, bgColor);
  const ids = DataStore.getRowIdentifiers(row, [xColumn], '');

  const labels = (ids ? ids.name : name).split('|');
  const mainLabel = labels?.[0];
  const subLabel = labels?.[1];

  set.label = mainLabel;
  set.subLabel = subLabel;
  set.uniqueId = name;

  set.changeType = [FormatHint.Percentage, FormatHint.Index].includes(yColumn.formatHint)
    ? ChangeType.percent
    : ChangeType.absolute;
  const values = row.values[yColumn.name] as (number | null)[];
  values?.forEach(val => {
    set.values.raw.push(val);
    set.values.fillColor.push(backgroundColor);
    set.values.textColor.push(textColor);
  });

  if (withChange) {
    const changeCol = yColumn as KpiWithChangeColumn;
    const changeValues = row.values[changeCol.changeName] as (number | null)[];
    changeValues?.forEach(val => {
      set.changes.raw.push(val);
      set.changes.textColor.push(getChangeColor(val));
    });
  }

  return set;
}

export function formatDataSets(
  valueSets: DataSet[],
  options: {
    yColumn: DataColumn;
    currency: string;
  }
) {
  const { yColumn, currency } = options;
  const { dataType, formatHint } = yColumn;
  const { changeDataType, changeFormatHint } = yColumn as KpiWithChangeColumn;

  valueSets.forEach(set => {
    set.values.raw.forEach(value => {
      const formatted = Formatting.format(value, {
        dataType,
        formatHint,
        currency,
      });
      set.values.formatted.push(formatted);
    });
    if (set.changes) {
      set.changes.raw.forEach(value => {
        const formatted = Formatting.format(value, {
          dataType: changeDataType,
          formatHint: changeFormatHint,
          isChange: true,
          currency,
        });
        set.changes.formatted.push(formatted);
      });
    }
  });
}

export function formatScaleValue(value: string | number, options: { yColumn: DataColumn }): string {
  const {
    yColumn: { dataType, formatHint },
  } = options;

  const formatted = Formatting.format(Number(value), {
    dataType: dataType,
    // change format hint to hide the currency symbol
    formatHint: formatHint !== FormatHint.Money ? formatHint : undefined,
  });
  return formatted;
}

export function formatTooltipValue(value: string | number, options: { yColumn: DataColumn; currency: string }): string {
  const {
    yColumn: { dataType, formatHint },
    currency,
  } = options;

  const formatted = Formatting.format(Number(value), {
    dataType: dataType,
    formatHint: formatHint,
    currency,
  });
  return formatted;
}

export function hasDataSetValues(sets: DataSet[]): boolean {
  return !sets.every(dataset => dataset.values.raw.every(value => value === null));
}

export function getWarningsOfColumn(columnName: string, fieldWarnings?: DataWarning[]): DataWarning[] {
  return fieldWarnings?.filter(([, { kpis }]: DataWarning) => kpis.some(({ name }) => name === columnName)) ?? [];
}

export function getChangeColor(value: number): string {
  return value < 0 ? ChartColors.negativeChange : ChartColors.positiveChange;
}

export function findDescriptionColumn(columns: DataColumn[], xColumnName: string): DataColumn | undefined {
  const xColumn = columns.find(col => col.name === xColumnName);

  if ((xColumn as IdentifierColumn)?.idField) {
    return xColumn;
  }

  const descriptionColumn = columns.find(
    col => col.relatedTo === xColumnName && col.relationship === ColumnRelationship.Description
  );

  if (descriptionColumn) {
    return descriptionColumn;
  }

  return xColumn;
}
